'use client';

import {
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type SyntheticEvent,
} from 'react';
import { Tooltip } from '@terra/ui-design-system';
import { Box } from '@terra/ui-styled-system';

type DisableableElement = ReactElement<{
  children?: ReactNode;
  disabled?: boolean;
  onClick?: (event: SyntheticEvent) => void;
  onPointerDown?: (event: SyntheticEvent) => void;
  onSelect?: (event: SyntheticEvent) => void;
}>;

const stopGatedAction = (event: SyntheticEvent) => {
  event.preventDefault();
  event.stopPropagation();
};

const disableGatedChild = (node: ReactNode): ReactNode => {
  if (!isValidElement(node)) {
    return node;
  }

  if (node.type === Tooltip) {
    return disableGatedChild((node.props as { children?: ReactNode }).children);
  }

  return cloneElement(node as DisableableElement, {
    disabled: true,
    onClick: stopGatedAction,
    onPointerDown: stopGatedAction,
    onSelect: stopGatedAction,
  });
};

export function ArchivedActionGate({
  isArchived,
  tooltip,
  children,
}: {
  isArchived: boolean;
  tooltip: string;
  children: ReactNode;
}) {
  if (!isArchived) {
    return children;
  }

  return (
    <Tooltip title={tooltip}>
      <Box
        display="inline-flex"
        cursor="not-allowed"
        position="relative"
        onClickCapture={stopGatedAction}
        onPointerDownCapture={stopGatedAction}
        onKeyDownCapture={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            stopGatedAction(event);
          }
        }}
      >
        {disableGatedChild(children)}
        <Box position="absolute" inset="0" aria-hidden />
      </Box>
    </Tooltip>
  );
}
